import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/fr";
import "dayjs/locale/nl";
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
/**
 * Resolves a user's local date (ISO yyyy-MM-dd) + time (HH:mm) + IANA timezone into the
 * absolute UTC instant the backend expects. Returns null if any input is missing or invalid.
 */
export function localScheduleToUtcIso(date, time, ianaTimezone) {
    if (!date || !time || !ianaTimezone)
        return null;
    const parsed = dayjs.tz(`${date} ${time}`, "YYYY-MM-DD HH:mm", ianaTimezone);
    return parsed.isValid() ? parsed.toISOString() : null;
}
/**
 * Splits an ISO UTC instant back into local date (yyyy-MM-dd) and time (HH:mm) for a given IANA timezone.
 * Returns empty strings when the input is missing.
 */
export function utcIsoToLocalSchedule(isoUtc, ianaTimezone) {
    if (!isoUtc || !ianaTimezone)
        return { date: "", time: "" };
    const local = dayjs.utc(isoUtc).tz(ianaTimezone);
    if (!local.isValid())
        return { date: "", time: "" };
    return { date: local.format("YYYY-MM-DD"), time: local.format("HH:mm") };
}
/**
 * Formats a UTC instant as "<localDate> <atLabel> <localTime>" in the given timezone using
 * the user's preferred date format. Returns null when the input is missing or invalid so the
 * caller can decide on a fallback label.
 */
export function formatScheduledAt(isoUtc, ianaTimezone, userDateFormat, locale, atLabel) {
    const local = utcIsoToLocalSchedule(isoUtc, ianaTimezone);
    if (!local.date || !local.time)
        return null;
    const displayDate = isoToDisplayDate(local.date, userDateFormat, locale) || local.date;
    return `${displayDate} ${atLabel} ${local.time}`;
}
/**
 * Splits a UTC instant into localized day-month-year and time parts for a humanized label
 * (e.g. `{ date: "5 sept. 2026", time: "12:53" }`). Month names follow the locale; the time
 * respects the 12/24h preference. Rendered in `ianaTimezone` when provided, otherwise UTC —
 * matching the campaign store's `FALLBACK_TIMEZONE` (R30) so a sub-line rendered before the
 * profile loads doesn't briefly use the browser zone. Returns null when the input is missing or
 * invalid so the caller can decide on a fallback (e.g. hide the row).
 */
export function formatLocalDateParts(isoUtc, ianaTimezone, locale, use12h = false) {
    if (!isoUtc)
        return null;
    const instant = dayjs.utc(isoUtc).tz(ianaTimezone || "UTC");
    if (!instant.isValid())
        return null;
    // dayjs only has the base locales loaded ("fr", "nl", built-in "en") — strip the region
    // subtag ("fr-FR" → "fr") so month names localize instead of falling back to English.
    const localized = instant.locale(locale.slice(0, 2).toLowerCase());
    return {
        date: localized.format("D MMM YYYY"),
        time: localized.format(use12h ? "hh:mm A" : "HH:mm"),
    };
}
/**
 * Day-only, year-less rendering of an instant in the campaign zone — e.g. `12 déc.`.
 *
 * The year-less form is deliberate and comes from the design: these labels sit inside a chart
 * whose axis already establishes the period, so repeating the year on every row is noise. Use
 * {@link formatLocalDateParts} when the surface has no such context.
 *
 * Returns an empty string for a missing or invalid input so the caller can hide the row.
 */
export function formatLocalDayShort(isoUtc, ianaTimezone, locale) {
    if (!isoUtc)
        return "";
    const instant = dayjs.utc(isoUtc).tz(ianaTimezone || "UTC");
    if (!instant.isValid())
        return "";
    // Same region-subtag strip as `formatLocalDateParts` — "fr-FR" would fall back to English.
    return instant.locale(locale.slice(0, 2).toLowerCase()).format("D MMM");
}
/**
 * Formats a date string using the user's preferred format from their profile.
 * The format token (e.g., "DD/MM/YYYY", "DD MMM YYYY") is stored as-is in the
 * `users.date_format` column and passed directly to dayjs.
 *
 * Falls back to "DD/MM/YYYY" when no user format is provided.
 */
export function formatDate(dateStr, locale, userDateFormat) {
    if (!dateStr)
        return "–";
    return dayjs(dateStr)
        .locale(locale.toLowerCase())
        .format(userDateFormat ?? "DD/MM/YYYY");
}
/**
 * Formats a date as a long, capitalized calendar heading — e.g. "Lundi 21 juin 2026" —
 * independent of the user's configurable `dateFormat` preference. Use for editorial
 * "today" headings, not for data fields (those follow `formatDate`/R30).
 */
export function formatLongDate(dateStr, locale) {
    if (!dateStr)
        return "–";
    const parsed = new Date(dateStr);
    if (Number.isNaN(parsed.getTime()))
        return "–";
    const formatted = new Intl.DateTimeFormat(locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    }).format(parsed);
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}
/**
 * Formats a date string (or Date object) with both date and time using the
 * user's preferred formats from their profile.
 *
 * @param date - ISO string or Date object to format
 * @param locale - i18n locale (e.g. "en", "fr", "nl")
 * @param userDateFormat - dayjs date token from profile (e.g. "DD/MM/YYYY")
 * @param userTimeFormat - "24" or "12" from profile (converted to "HH:mm" / "hh:mm A")
 * @param userTimeZone - optional IANA timezone id (e.g. "Europe/Paris"); when provided, appends the short TZ abbreviation
 */
export function formatDateTime(date, locale, userDateFormat, userTimeFormat, userTimeZone) {
    if (!date)
        return "–";
    const dateFmt = userDateFormat ?? "DD/MM/YYYY";
    const timeFmt = userTimeFormat === "12" ? "hh:mm A" : "HH:mm";
    let result = dayjs(date).locale(locale.toLowerCase()).format(`${dateFmt} ${timeFmt}`);
    if (userTimeZone) {
        const tz = getShortTimeZone(date, locale, userTimeZone);
        if (tz) {
            result += ` (${tz})`;
        }
    }
    return result;
}
/**
 * Returns a short timezone abbreviation (e.g. "CET", "GMT+1") for the given
 * date, locale and IANA timezone id using the native Intl API.
 */
function getShortTimeZone(date, locale, timeZone) {
    try {
        const d = date instanceof Date ? date : new Date(date);
        return (new Intl.DateTimeFormat(locale, {
            timeZone: timeZone ?? undefined,
            timeZoneName: "short",
        })
            .formatToParts(d)
            .find((p) => p.type === "timeZoneName")?.value ?? "");
    }
    catch {
        // Intl.DateTimeFormat throws for unsupported/invalid timeZone — degrade silently
        return "";
    }
}
/** Formats an ISO year-month string (yyyy-MM or yyyy-MM-dd) to a localized "Month Year" label (e.g. "March 2024"). */
export function formatMonthYear(isoYearMonth, locale) {
    return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${isoYearMonth.slice(0, 7)}-01`));
}
/** Converts an ISO date string (yyyy-MM-dd or ISO 8601) to the DD/MM/YYYY display format used by DatePickerInput. */
export function isoToPickerDate(iso) {
    if (!iso)
        return null;
    const parsed = dayjs(iso);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY") : null;
}
/** Converts a DD/MM/YYYY picker value back to ISO yyyy-MM-dd for the BFF. */
export function pickerDateToIso(display) {
    if (!display)
        return null;
    const parsed = dayjs(display, "DD/MM/YYYY", true);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
}
/**
 * Converts a user date format token to a flowbite datepicker format string.
 * Named-month formats have no flowbite equivalent → falls back to locale numeric convention.
 */
export function toDatePickerFormat(userDateFormat, locale) {
    if (userDateFormat === "DD/MM/YYYY")
        return "dd/mm/yyyy";
    if (userDateFormat === "YYYY-MM-DD")
        return "yyyy-mm-dd";
    const localeDefaults = {
        "en-US": "mm/dd/yyyy",
        "fr-FR": "dd/mm/yyyy",
        "nl-NL": "dd-mm-yyyy",
    };
    return (locale && localeDefaults[locale]) ?? "dd/mm/yyyy";
}
/**
 * Returns the user-format → dayjs parse token mapping that mirrors `toDatePickerFormat`.
 * Used to round-trip DatePickerInput text back into an ISO (yyyy-MM-dd) wire value.
 */
function toDayjsParseFormat(userDateFormat, locale) {
    if (userDateFormat === "DD/MM/YYYY")
        return "DD/MM/YYYY";
    if (userDateFormat === "YYYY-MM-DD")
        return "YYYY-MM-DD";
    const localeDefaults = {
        "en-US": "MM/DD/YYYY",
        "fr-FR": "DD/MM/YYYY",
        "nl-NL": "DD-MM-YYYY",
    };
    return (locale && localeDefaults[locale]) ?? "DD/MM/YYYY";
}
/**
 * Parses a display-format date string produced by DatePickerInput into ISO 8601 (yyyy-MM-dd).
 * Returns an empty string when the input is empty or unparseable — callers must decide how to
 * surface that (block save, show field error, etc.).
 */
export function displayDateToIso(displayValue, userDateFormat, locale) {
    if (!displayValue)
        return "";
    const parsed = dayjs(displayValue, toDayjsParseFormat(userDateFormat, locale), true);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
}
/** The picked day shifted by `dayOffset` days, as a calendar date `YYYY-MM-DD` — a date-only column's day bound. */
export function displayDateToIsoDay(display, userDateFormat, locale, dayOffset) {
    if (!display)
        return undefined;
    const parsed = dayjs(display, toDayjsParseFormat(userDateFormat, locale), true);
    return parsed.isValid() ? parsed.add(dayOffset, "day").format("YYYY-MM-DD") : undefined;
}
/**
 * The picked day's LOCAL midnight (shifted by `dayOffset` days) as a UTC instant — a list filter's day bound (R51).
 * `dayOffset` 1 gives the exclusive upper bound of an inclusive day range.
 */
export function displayDateToLocalMidnightIso(display, userDateFormat, locale, dayOffset) {
    if (!display)
        return undefined;
    const iso = displayDateToIso(display, userDateFormat, locale);
    if (!iso)
        return undefined;
    const [year, month, day] = iso.split("-").map(Number);
    if (!year || !month || !day)
        return undefined;
    return new Date(year, month - 1, day + dayOffset, 0, 0, 0, 0).toISOString();
}
/**
 * Formats a UTC instant as a time-only string (HH:mm or hh:mm A) respecting the
 * user's 12/24h preference. Returns "–" when the input is missing or invalid.
 *
 * @param date - ISO string or Date object
 * @param locale - i18n locale (e.g. "en", "fr", "nl")
 * @param userTimeFormat - "24" or "12" from profile; defaults to 24h
 */
export function formatTime(date, locale, userTimeFormat) {
    if (!date)
        return "–";
    const timeFmt = userTimeFormat === "12" ? "hh:mm A" : "HH:mm";
    const parsed = dayjs(date);
    if (!parsed.isValid())
        return "–";
    return parsed.locale(locale.slice(0, 2).toLowerCase()).format(timeFmt);
}
/**
 * Today's date rendered in the user's display format — for binding a DatePickerInput's
 * `max-date` so the calendar disables future dates while leaving today selectable
 * (flowbite-datepicker's `maxDate` boundary is inclusive).
 */
export function todayDisplayDate(userDateFormat, locale) {
    return dayjs().format(toDayjsParseFormat(userDateFormat, locale));
}
/**
 * Today's date as an ISO (yyyy-MM-dd) calendar string, built from LOCAL date parts (not
 * `toISOString`, which is UTC and skews ±1 day off-UTC) — mirrors the contact module's own
 * `useContactIdentityForm` today-guard so both surfaces agree with the user's calendar.
 */
export function todayIso() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}
/** True when an ISO (yyyy-MM-dd) date string is strictly after today — today itself is not "in the future". */
export function isIsoDateInFuture(isoDate) {
    return isoDate > todayIso();
}
/**
 * Renders an ISO (yyyy-MM-dd) date using the user's display format — the inverse of
 * `displayDateToIso`. Returns an empty string for empty input.
 */
export function isoToDisplayDate(isoValue, userDateFormat, locale) {
    if (!isoValue)
        return "";
    const parsed = dayjs(isoValue, "YYYY-MM-DD", true);
    return parsed.isValid() ? parsed.format(toDayjsParseFormat(userDateFormat, locale)) : "";
}
